import React, { useEffect, useRef } from 'react';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import type { CoverPicture } from '../render/drawBackground';
import { renderCoverDoc } from '../render/renderCoverDoc';

interface CoverCanvasProps {
  doc: CoverDoc;
  /** The side of the cover on screen in CSS pixels. */
  side: number;
  picture: CoverPicture | null;
  /** Changes when the fonts load: the cover draws again in the right faces. */
  isFontsReady: boolean;
  /** The text box that the user types in: the canvas leaves it out. */
  hiddenTextId: string | null;
  className?: string;
}

/**
 * The cover on the stage: the one renderer on a canvas at the pixel density of the screen (at most
 * 2). One draw per animation frame, so a drag draws as fast as the screen and no faster.
 */
const CoverCanvasComponent: React.FC<CoverCanvasProps> = (props) => {
  const { doc, side, picture, isFontsReady, hiddenTextId, className } = props;
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const element = canvas.current;
    if (element === null) {
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      const ctx = element.getContext('2d');
      if (ctx === null) {
        return;
      }
      const ratio = Math.min(2, window.devicePixelRatio > 0 ? window.devicePixelRatio : 1);
      const pixels = Math.round(side * ratio);
      if (element.width !== pixels) {
        element.width = pixels;
        element.height = pixels;
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, pixels, pixels);
      renderCoverDoc(ctx, doc, pixels, { picture }, { hiddenTextId });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [doc, hiddenTextId, isFontsReady, picture, side]);

  return <canvas ref={canvas} className={className} style={{ width: side, height: side }} aria-hidden />;
};

export const CoverCanvas = TypedMemo(CoverCanvasComponent);
